// EditDataModal component - Modal for editing bus operation data

import { useState, useEffect } from "react";
import Modal from "../FuelManagement/Modal";
import BusStopManager from "./BusStopManager";
import SearchableSelect from "../common/SearchableSelect";

const EditDataModal = ({ isOpen, onClose, onSubmit, record }) => {
  const [formData, setFormData] = useState({
    date: "",
    bus_line: "",
    bus: "",
    license_plate: "",
    driver_name: "",
    tickets: "",
    daypass: "",
    daypass_sold: "",
    daypass_income: "",
    trips: "1",
  });

  const [busStops, setBusStops] = useState([{ name: "", passengers: "" }]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (record) {
      setFormData({
        date: record.date ? record.date.split("T")[0] : "",
        bus_line: record.bus_line || "",
        bus: record.bus || "",
        license_plate: record.license_plate || "",
        driver_name: record.driver_name || "",
        tickets: (record.tickets || 0).toString(),
        daypass: (record.daypass || 0).toString(),
        daypass_sold: (record.daypass_sold || 0).toString(),
        daypass_income: (record.daypass_income || 0).toString(),
        trips: (record.trips || 1).toString(),
      });

      // Handle bus stops
      if (record.bus_stops && record.bus_stops.length > 0) {
        setBusStops(
          record.bus_stops.map((stop) => ({
            name: stop.stop_name || "",
            passengers: (stop.passengers || 0).toString(),
          })),
        );
      } else {
        setBusStops([{ name: "", passengers: "" }]);
      }

      setErrors({});
    }
  }, [record]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = "กรุณาเลือกวันที่";
    }
    if (!formData.bus_line.trim()) {
      newErrors.bus_line = "กรุณากรอกสายรถ";
    }
    if (!formData.license_plate.trim()) {
      newErrors.license_plate = "กรุณากรอกทะเบียนรถ";
    }
    if (!formData.driver_name.trim()) {
      newErrors.driver_name = "กรุณากรอกชื่อพนักงานขับรถ";
    }
    if (!formData.tickets || parseInt(formData.tickets) < 0) {
      newErrors.tickets = "กรุณากรอกจำนวนตั๋วที่ถูกต้อง";
    }
    if (!formData.daypass || parseInt(formData.daypass) < 0) {
      newErrors.daypass = "กรุณากรอกจำนวน Daypass ที่ถูกต้อง";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Filter valid bus stops
    const validBusStops = busStops
      .filter((stop) => stop.name.trim() && parseInt(stop.passengers) > 0)
      .map((stop) => ({
        stop_name: stop.name.trim(),
        passengers: parseInt(stop.passengers),
      }));

    const data = {
      date: formData.date,
      bus_line: formData.bus_line,
      bus: formData.bus,
      license_plate: formData.license_plate,
      driver_name: formData.driver_name,
      tickets: parseInt(formData.tickets) || 0,
      daypass: parseInt(formData.daypass) || 0,
      daypass_sold: parseInt(formData.daypass_sold) || 0,
      daypass_income: parseFloat(formData.daypass_income) || 0,
      trips: parseInt(formData.trips) || 1,
      bus_stops: validBusStops,
    };

    onSubmit(record.id, data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="แก้ไขข้อมูลรถ" size="xl">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Date and Bus Line Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              วันที่ <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className={`w-full px-4 py-2.5 border ${
                errors.date ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-500">{errors.date}</p>
            )}
          </div>

          {/* Bus Line */}
          <div>
            <SearchableSelect
              name="bus_line"
              value={formData.bus_line}
              onChange={handleInputChange}
              options={[
                { value: "สนามบิน - ราไวย์", label: "สนามบิน - ราไวย์" },
                { value: "บขส.1 - ป่าตอง", label: "บขส.1 - ป่าตอง" },
                { value: "รอบเมืองเก่า", label: "รอบเมืองเก่า" },
              ]}
              placeholder="เลือกสายรถ..."
              label={
                <>
                  สายรถ <span className="text-red-500">*</span>
                </>
              }
              icon={
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                    clipRule="evenodd"
                  />
                </svg>
              }
            />
            {errors.bus_line && (
              <p className="mt-1 text-sm text-red-500">{errors.bus_line}</p>
            )}
          </div>
        </div>

        {/* Bus Number, License Plate, Driver Name Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Bus */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เลขรถ
            </label>
            <input
              type="text"
              name="bus"
              value={formData.bus}
              onChange={handleInputChange}
              placeholder="เช่น 1"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* License Plate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ทะเบียนรถ <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="license_plate"
              value={formData.license_plate}
              onChange={handleInputChange}
              placeholder="เช่น กข-1234"
              className={`w-full px-4 py-2.5 border ${
                errors.license_plate ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
            />
            {errors.license_plate && (
              <p className="mt-1 text-sm text-red-500">
                {errors.license_plate}
              </p>
            )}
          </div>

          {/* Driver */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              พนักงานขับรถ <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="driver_name"
              value={formData.driver_name}
              onChange={handleInputChange}
              placeholder="ชื่อพนักงานขับรถ"
              className={`w-full px-4 py-2.5 border ${
                errors.driver_name ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
            />
            {errors.driver_name && (
              <p className="mt-1 text-sm text-red-500">{errors.driver_name}</p>
            )}
          </div>
        </div>

        {/* Tickets, Daypass, Trips Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Tickets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              จำนวนตั๋ว <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="tickets"
              value={formData.tickets}
              onChange={handleInputChange}
              placeholder="0"
              min="0"
              className={`w-full px-4 py-2.5 border ${
                errors.tickets ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
            />
            {errors.tickets && (
              <p className="mt-1 text-sm text-red-500">{errors.tickets}</p>
            )}
          </div>

          {/* Daypass */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Daypass <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="daypass"
              value={formData.daypass}
              onChange={handleInputChange}
              placeholder="0"
              min="0"
              className={`w-full px-4 py-2.5 border ${
                errors.daypass ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
            />
            {errors.daypass && (
              <p className="mt-1 text-sm text-red-500">{errors.daypass}</p>
            )}
          </div>

          {/* Trips */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              จำนวนเที่ยว
            </label>
            <input
              type="number"
              name="trips"
              value={formData.trips}
              onChange={handleInputChange}
              placeholder="1"
              min="1"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Daypass Sold and Daypass Income Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Daypass Sold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Daypass ขาย
            </label>
            <input
              type="number"
              name="daypass_sold"
              value={formData.daypass_sold}
              onChange={handleInputChange}
              placeholder="0"
              min="0"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Daypass Income */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              รายได้ Daypass (บาท)
            </label>
            <input
              type="number"
              name="daypass_income"
              value={formData.daypass_income}
              onChange={handleInputChange}
              placeholder="0"
              min="0"
              step="0.01"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Bus Stops Manager */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ป้ายรถเมล์และผู้โดยสาร
          </label>
          <BusStopManager busStops={busStops} setBusStops={setBusStops} />
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">คำแนะนำ:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>ฟิลด์ที่มีเครื่องหมาย * จำเป็นต้องกรอก</li>
                <li>สามารถเพิ่มป้ายรถเมล์ได้หลายป้าย</li>
                <li>ข้อมูลจะถูกบันทึกทันทีหลังจากกดปุ่มบันทึก</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors duration-200"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 shadow-lg shadow-blue-500/30"
          >
            บันทึกการแก้ไข
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditDataModal;
